import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CmsPopup from '../../../../components/CmsPopup';
import { AppTextField } from '../../../../customComponents/AppTextField';
import { AppNumberTextField } from '../../../../customComponents/AppNumberTextField';
import { AppDropdownField, DropdownOption } from '../../../../customComponents/AppDropdownField';
import Switch from '../../../../components/Switch/Switch';
import CustomTiptapEditor from '../../../../components/Editor/CustomTiptapEditor';
import { CmsPopupBtnType } from '../../../../components/CmsPopupBtn';
import CmsPopupBtn from '../../../../components/CmsPopupBtn';
import Modal from '../../../../components/Modal';
import MultiImageAdd from '../../../../components/MultiImageAdd';
import AuctioneerSearchPopup from '../../../../customComponents/AuctioneerSearchPopup';
import { uploadCommonFile, uploadEditorImages, processEditorContent } from '../../../../lib/api/common/commonApi';
import { devLog, devError } from '../../../../utils/devLogger';
import { MAJOR_CATEGORY_OPTIONS, MINOR_CATEGORY_OPTIONS } from '../../../../constants/categories';

// 서버 파일명을 실제 이미지 URL로 변환하는 함수
const getImageSrc = (imageUrl?: string | null) => {
  if (!imageUrl || imageUrl.trim() === "") {
    return "";
  }
  
  // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
  if (imageUrl.startsWith("http") || imageUrl.startsWith("blob:")) {
    return imageUrl;
  }
  
  // 절대 경로인 경우 (/로 시작)
  if (imageUrl.startsWith("/")) {
    return `${process.env.REACT_APP_API_HOST}${imageUrl}`;
  }
  
  // 단순 파일명인 경우 (서버에서 받은 파일명)
  return `${process.env.REACT_APP_API_HOST}/${imageUrl}`;
};

// LOT 폼 데이터 타입
export interface LotFormData {
  auctioneerName: string;
  auctioneerId: string;
  majorCategory: string;
  minorCategory: string;
  artworkName: string;
  startPriceKRW: number | null;
  thumbnailImages: string[];
  artworkDetail: string;
  disabled: boolean;
}

interface LotFormPopupProps {
  isOpen: boolean;
  isEditMode: boolean;
  formData: LotFormData;
  formErrors: Partial<Record<keyof LotFormData, string>>;
  onClose: () => void;
  onSave: (processedFormData?: Partial<LotFormData>) => void; // 처리된 데이터를 전달할 수 있도록 수정
  onDelete?: () => void;
  onInputChange: (field: keyof LotFormData, value: string | number | string[] | boolean | null) => void;
  onImageUpload: (files: File[]) => void;
  readonlyAuctioneer?: boolean; // 경매사 필드를 readonly로 만들지 여부
  auctionData?: { // 경매 정보 추가
    preBidStartAt?: string;
    auctionStartAt?: string;
  };
}

const LotFormPopup: React.FC<LotFormPopupProps> = ({
  isOpen,
  isEditMode,
  formData,
  formErrors,
  onClose,
  onSave,
  onDelete,
  onInputChange,
  onImageUpload,
  readonlyAuctioneer = false,
  auctionData,
}) => {
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imageUploading, setImageUploading] = useState(false); // 이미지 업로드 중 상태

  // 삭제 확인 모달 상태
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // 스위치 확인 모달 상태
  const [isSwitchModalOpen, setIsSwitchModalOpen] = useState(false);
  const [pendingSwitchValue, setPendingSwitchValue] = useState<boolean | null>(null);

  // 경매사 검색 팝업 상태
  const [isAuctioneerPopupOpen, setIsAuctioneerPopupOpen] = useState(false);

  // 소분류 카테고리 옵션 (대분류에 따라 변경)
  const [minorCategoryOptions, setMinorCategoryOptions] = useState<DropdownOption[]>([]);

  // 시간 기반 제약사항 계산
  const currentTime = new Date();
  const preBidStartTime = auctionData?.preBidStartAt ? new Date(auctionData.preBidStartAt) : null;
  const auctionStartTime = auctionData?.auctionStartAt ? new Date(auctionData.auctionStartAt) : null;
  
  const isPreBidStarted = preBidStartTime ? currentTime >= preBidStartTime : false;
  const isAuctionStarted = auctionStartTime ? currentTime >= auctionStartTime : false;
  
  // 폼 수정 가능 여부 (사전입찰 시작 전까지만 가능)
  const isFormEditable = !isPreBidStarted;
  
  // 철회 섹션 수정 가능 여부 (라이브 시작 전까지만 가능)
  const isWithdrawalEditable = !isAuctionStarted;

  // 대분류 변경 시 소분류 옵션 업데이트
  useEffect(() => {
    if (formData.majorCategory) {
      const newOptions = MINOR_CATEGORY_OPTIONS[formData.majorCategory] || [];
      setMinorCategoryOptions(newOptions);
      
      // 현재 선택된 소분류가 새로운 옵션에 없는 경우에만 초기화 (수정 모드 고려)
      const currentMinorCategory = formData.minorCategory;
      if (currentMinorCategory && !newOptions.find(option => option.value === currentMinorCategory)) {
        onInputChange('minorCategory', '');
      }
    } else {
      setMinorCategoryOptions([]);
      // 대분류가 없을 때만 소분류 초기화
      if (formData.minorCategory) {
        onInputChange('minorCategory', '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.majorCategory, onInputChange]); // minorCategory는 의도적으로 제외

  // 경매사 선택 해제 핸들러
  const handleAuctioneerClear = () => {
    onInputChange('auctioneerId', '');
    onInputChange('auctioneerName', '');
  };

  // 경매사 선택 핸들러
  const handleAuctioneerSelect = (auctioneer: any) => {
    onInputChange('auctioneerId', auctioneer.index.toString());
    onInputChange('auctioneerName', auctioneer.name);
    setIsAuctioneerPopupOpen(false);
  };

  // 여러 이미지 파일 선택 핸들러 (한번에 처리)
  const handleImagesAdd = async (files: File[]) => {
    devLog('🖼️ LOT 여러 이미지 추가:', files.length, '장');
    
    // 업로드 중이면 중복 업로드 방지
    if (imageUploading) {
      toast.warning("이미 이미지 업로드가 진행 중입니다. 잠시 후 다시 시도해 주세요.");
      return;
    }
    
    // 각 파일 검증
    const validFiles: File[] = [];
    for (const file of files) {
      // 파일 크기 검증
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: 파일 크기가 10MB를 초과할 수 없습니다.`);
        continue;
      }
      
      // 파일 타입 검증
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name}: 이미지 파일만 업로드할 수 있습니다.`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    if (validFiles.length === 0) {
      return;
    }
    
    // 이미지 개수 제한 확인
    const remainingSlots = 3 - formData.thumbnailImages.length;
    if (remainingSlots <= 0) {
      toast.error("최대 3장의 이미지만 업로드할 수 있습니다.");
      return;
    }
    
    const filesToUpload = validFiles.slice(0, remainingSlots);
    if (filesToUpload.length < validFiles.length) {
      toast.warning(`최대 3장까지만 업로드됩니다. ${filesToUpload.length}장이 선택되었습니다.`);
    }
    
    setImageUploading(true); // 업로드 시작
    
    // 업로드 전 상태 저장 (인덱스 계산용)
    const preUploadImageUrls = [...formData.thumbnailImages];
    const preUploadImageCount = preUploadImageUrls.length;
    const preUploadFileCount = imageFiles.length;
    
    // 미리보기 즉시 생성 (업로드와 별개로)
    const newImageUrls = filesToUpload.map(file => URL.createObjectURL(file));
    const updatedImageUrls = [...preUploadImageUrls, ...newImageUrls];
    onInputChange('thumbnailImages', updatedImageUrls);
    
    // 로컬 파일 상태 업데이트
    const newFiles = [...imageFiles, ...filesToUpload];
    setImageFiles(newFiles);
    onImageUpload(newFiles);
    
    try {
      // 여러 파일을 FormData로 감싸서 서버에 업로드
      const uploadFormData = new FormData();
      filesToUpload.forEach((file, index) => {
        uploadFormData.append('file', file); // 모든 파일을 'file' 필드로 추가
      });
      
      devLog('📤 LOT 여러 이미지 업로드 요청:', filesToUpload.length, '장');
      
      // uploadCommonFile 호출
      const uploadRes = await uploadCommonFile(uploadFormData, true);
      devLog('📤 LOT 여러 이미지 업로드 응답:', uploadRes);
      
      const res = Array.isArray(uploadRes) ? uploadRes[0] : uploadRes;
      if (res?.message === "success") {
        const uploadedData = res?.data ?? [];
        
        if (uploadedData.length > 0) {
          devLog('업로드된 파일 정보들:', uploadedData);
          
          // 업로드된 파일들의 서버 파일명으로 미리보기 URL들을 교체
          // 업로드 전 상태를 기준으로 인덱스 계산
          const updatedImageUrlsWithServerNames = [...updatedImageUrls];
          const failedRelativeIndexes: number[] = [];

          filesToUpload.forEach((file, index) => {
            const fileInfo = uploadedData[index];
            const targetIndex = preUploadImageCount + index; // 새로 추가된 이미지들의 인덱스

            if (!fileInfo) {
              failedRelativeIndexes.push(index);
              devError('LOT 이미지 업로드 실패 - 응답 데이터 없음:', { fileName: file.name, index });
              return;
            }

            const fileName = typeof fileInfo === 'string' ? fileInfo : fileInfo?.fileName || fileInfo?.index;
            
            if (fileName !== undefined && fileName !== null && targetIndex < updatedImageUrlsWithServerNames.length) {
              updatedImageUrlsWithServerNames[targetIndex] = String(fileName);
              devLog(`✅ 이미지 ${targetIndex} 인덱스: 미리보기 URL → 서버 파일명 (${fileName})`);
            } else {
              failedRelativeIndexes.push(index);
              devError('LOT 이미지 업로드 실패 - 파일명 없음:', { fileInfo, index });
            }
          });

          if (failedRelativeIndexes.length > 0) {
            const failedTargetIndexes = failedRelativeIndexes.map(relativeIndex => preUploadImageCount + relativeIndex);
            const finalImageUrls = updatedImageUrlsWithServerNames.filter((_, idx) => !failedTargetIndexes.includes(idx));

            const failedFileIndexes = failedRelativeIndexes.map(relativeIndex => preUploadFileCount + relativeIndex);
            const finalFiles = newFiles.filter((_, idx) => !failedFileIndexes.includes(idx));

            onInputChange('thumbnailImages', finalImageUrls);
            setImageFiles(finalFiles);
            onImageUpload(finalFiles);

            toast.error(`일부 이미지 업로드에 실패하여 제외되었습니다. (${failedRelativeIndexes.length}장)`);
          } else {
            onInputChange('thumbnailImages', updatedImageUrlsWithServerNames);
          }
        } else {
          toast.error("업로드된 파일 정보가 없습니다.");
          // 업로드 실패 시 추가된 이미지들 제거 (업로드 전 상태로 롤백)
          onInputChange('thumbnailImages', preUploadImageUrls);
          
          const rollbackFiles = imageFiles.slice(0, imageFiles.length - filesToUpload.length);
          setImageFiles(rollbackFiles);
        }
      } else {
        const errorMessage = res?.error?.message || "LOT 이미지 업로드에 실패했습니다.";
        devError('업로드 실패:', errorMessage);
        toast.error(errorMessage);
        // 업로드 실패 시 추가된 이미지들 제거 (업로드 전 상태로 롤백)
        onInputChange('thumbnailImages', preUploadImageUrls);
        
        const rollbackFiles = imageFiles.slice(0, imageFiles.length - filesToUpload.length);
        setImageFiles(rollbackFiles);
      }
    } catch (error) {
      devError('LOT 여러 이미지 업로드 실패:', error);
      toast.error("LOT 이미지 업로드에 실패했습니다.");
      // 업로드 실패 시 추가된 이미지들 제거 (업로드 전 상태로 롤백)
      onInputChange('thumbnailImages', preUploadImageUrls);
      
      const rollbackFiles = imageFiles.slice(0, imageFiles.length - filesToUpload.length);
      setImageFiles(rollbackFiles);
    } finally {
      setImageUploading(false); // 업로드 완료 후 상태 초기화
    }
  };

  // 단일 이미지 파일 선택 핸들러 (기존 유지, 하나씩 추가할 때 사용)
  const handleImageAdd = async (file: File) => {
    // 여러 이미지 핸들러로 위임
    await handleImagesAdd([file]);
  };

  // 이미지 삭제 핸들러
  const handleImageDelete = (index: number) => {
    // 사전입찰 시작 후에는 이미지 삭제 불가
    if (!isFormEditable) {
      return;
    }
    
    const newImages = [...formData.thumbnailImages];
    newImages.splice(index, 1);
    onInputChange('thumbnailImages', newImages);

    const newFiles = [...imageFiles];
    newFiles.splice(index, 1);
    setImageFiles(newFiles);
  };

  // 에디터 내용 변경 핸들러
  const handleEditorChange = (content: string) => {
    if (isFormEditable) {
      onInputChange('artworkDetail', content);
    }
  };

  // 스위치 변경 핸들러 (모달 확인 후 실제 변경)
  const handleSwitchChange = (checked: boolean) => {
    setPendingSwitchValue(checked);
    setIsSwitchModalOpen(true);
  };

  // 스위치 변경 확인
  const handleSwitchConfirm = async () => {
    if (pendingSwitchValue !== null) {
      try {
        // disabled 값만 업데이트하여 저장
        onSave({
          disabled: pendingSwitchValue,
        });
        
        // 로컬 상태도 업데이트
        onInputChange('disabled', pendingSwitchValue);
        
        // 성공 메시지
        toast.success(pendingSwitchValue ? 'LOT이 철회되었습니다.' : 'LOT이 재개되었습니다.');
      } catch (error) {
        devError('LOT 상태 업데이트 실패:', error);
        toast.error('LOT 상태 업데이트에 실패했습니다.');
      }
    }
    setIsSwitchModalOpen(false);
    setPendingSwitchValue(null);
  };

  // 스위치 변경 취소
  const handleSwitchCancel = () => {
    setIsSwitchModalOpen(false);
    setPendingSwitchValue(null);
  };

  // 에디터 저장 시 이미지 처리 함수 (NoticePopup과 동일한 로직)
  const processEditorImages = async (editorContent: string): Promise<string> => {
    try {
      // blob URL이 있는지 확인하고 처리
      const blobUrls = editorContent.match(/src="blob:[^"]+"/g) || [];
      let processedContent = editorContent;
      
      if (blobUrls.length > 0) {
        devLog('🖼️ 업로드할 이미지 발견:', blobUrls.length);
        
        // blob URL에서 File 객체들 추출
        const blobFiles: File[] = [];
        
        for (const blobUrlMatch of blobUrls) {
          const blobUrl = blobUrlMatch.replace('src="', '').replace('"', '');
          try {
            const response = await fetch(blobUrl);
            const blob = await response.blob();
            
            let fileExtension = blob.type.split('/')[1] || 'jpg';
            if (fileExtension.includes('+')) {
              fileExtension = fileExtension.split('+')[0];
            }
            
            const fileName = `editor-image-${Date.now()}.${fileExtension}`;
            const file = new File([blob], fileName, { type: blob.type });
            blobFiles.push(file);
          } catch (error) {
            devLog('blob URL 처리 실패:', { blobUrl, error });
          }
        }
        
        // 이미지 업로드 및 콘텐츠 변환
        if (blobFiles.length > 0) {
          const uploadResponse = await uploadEditorImages(blobFiles);
          processedContent = await processEditorContent(editorContent, uploadResponse.data);
        }
      }

      return processedContent;
    } catch (error) {
      devLog('이미지 처리 중 오류:', error);
      toast.error('이미지 처리 중 오류가 발생했습니다.');
      return editorContent; // 오류 발생 시 원본 반환
    }
  };

  // 내부 저장 핸들러 (이미지 처리 후 외부 onSave 호출)
  const handleInternalSave = async () => {
    try {
      // 에디터 콘텐츠에서 이미지 처리
      const processedArtworkDetail = await processEditorImages(formData.artworkDetail);
      
      // 처리된 데이터로 외부 onSave 호출
      onSave({
        artworkDetail: processedArtworkDetail,
      });
    } catch (error) {
      devLog('저장 중 오류:', error);
      toast.error('저장 중 오류가 발생했습니다.');
    }
  };

  // 삭제 확인 핸들러
  const handleDeleteConfirm = () => {
    setIsDeleteModalOpen(false);
    if (onDelete) {
      onDelete();
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  };

  const leftButtons = isEditMode && onDelete ? [{
    label: '삭제',
    onClick: () => setIsDeleteModalOpen(true),
    type: CmsPopupBtnType.PRIMARY,
    disabled: !isFormEditable, // 사전입찰 시작 후 삭제 불가
  }] : [];

  const rightButtons = [
    {
      label: '저장',
      onClick: handleInternalSave,
      type: CmsPopupBtnType.PRIMARY,
      disabled: !isFormEditable, // 사전입찰 시작 후 저장 불가
    },
    {
      label: '닫기',
      onClick: onClose,
      type: CmsPopupBtnType.SECONDARY,
    },
  ];

  return (
    <CmsPopup
      isOpen={isOpen}
      onClose={onClose}
      isWide={true}
      width={'1200px'}
      showRequiredMark={true}
      leftButtons={leftButtons}
      rightButtons={rightButtons}
    >
      <Wrapper>
        {/* 제약사항 메시지 */}
        {isEditMode && isPreBidStarted && (
          <RestrictionMessage>
            <RestrictionIcon>⚠️</RestrictionIcon>
            <RestrictionText>
              {isAuctionStarted 
                ? '라이브가 시작되어서 정보 수정 및 철회가 불가능합니다.' 
                : '사전입찰이 시작되어서 정보 수정이 불가능합니다.'}
            </RestrictionText>
          </RestrictionMessage>
        )}
        
        <Container>
          <LeftSection>
            <SectionTitleRow>
              <SectionTitle>LOT 등록상세</SectionTitle>
              <RequiredIndicator>*필수항목</RequiredIndicator>
            </SectionTitleRow>
            
            {/* LOT 경매 철회 여부 섹션 - 수정 모드일 때만 표시 */}
            {isEditMode && (
              <Section>
                <WithdrawalHeaderRow>
                  <SubSectionTitle>LOT 경매 철회 여부</SubSectionTitle>
                  <Switch
                    checked={formData.disabled}
                    onChange={handleSwitchChange}
                    size="medium"
                    disabled={!isWithdrawalEditable} // 라이브 시작 후 철회 섹션 수정 불가
                  />
                </WithdrawalHeaderRow>
                <WithdrawalNotice>
                  출품자 요청이나 내부 사정으로 경매를 중단할 경우 토글을 활성화하세요. 라이브 시작 시 철회·재개 여부에 따라 안내 푸시가 자동 발송됩니다.
                </WithdrawalNotice>
              </Section>
            )}
            
            {/* LOT 경매분류 섹션 */}
            <Section>
              <SubSectionTitle>LOT 경매분류</SubSectionTitle>
              
              <FormGroup>
                <div 
                  onClick={() => !(readonlyAuctioneer || !isFormEditable) && setIsAuctioneerPopupOpen(true)}
                  style={{ 
                    cursor: (readonlyAuctioneer || !isFormEditable) ? 'default' : 'pointer',
                    position: 'relative'
                  }}
                >
                  <AppTextField
                    label="*경매사"
                    value={formData.auctioneerName}
                    onChange={() => {}} // readOnly
                    placeholder={readonlyAuctioneer ? "선택된 경매사" : "경매사를 선택하세요"}
                    readOnly
                    errorMessage={formErrors.auctioneerName}
                  />
                  {/* {formData.auctioneerName && !(readonlyAuctioneer || !isFormEditable) && (
                    <IconButton
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleAuctioneerClear();
                      }}
                      size="small"
                      style={{ 
                        position: 'absolute', 
                        right: '8px', 
                        top: '50%', 
                        transform: 'translateY(-50%)',
                        zIndex: 1
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  )} */}
                </div>
              </FormGroup>

              <FormGroup>
                <AppDropdownField
                  label="*대분류"
                  value={formData.majorCategory}
                  onChange={(value) => onInputChange('majorCategory', value)}
                  options={MAJOR_CATEGORY_OPTIONS}
                  placeholder="대분류를 선택하세요"
                  errorMessage={formErrors.majorCategory}
                  disabled={!isFormEditable} // 사전입찰 시작 후 비활성화
                />
              </FormGroup>

              <FormGroup>
                <AppDropdownField
                  label="*소분류"
                  value={formData.minorCategory}
                  onChange={(value) => onInputChange('minorCategory', value)}
                  options={minorCategoryOptions}
                  placeholder="소분류를 선택하세요"
                  errorMessage={formErrors.minorCategory}
                  disabled={!formData.majorCategory || !isFormEditable} // 대분류 미선택 또는 사전입찰 시작 후 비활성화
                />
              </FormGroup>
            </Section>

            {/* LOT 정보 섹션 */}
            <Section>
              <SubSectionTitle>LOT 정보</SubSectionTitle>
              
              <ImageUploadField>
                <ImageLabel>*썸네일 이미지 등록</ImageLabel>
                <ImageFieldContainer>
                  <ImageScrollContainer>
                    <MultiImageAdd
                      imageUrls={formData.thumbnailImages.map(getImageSrc)}
                      onImageAdd={handleImageAdd}
                      onImagesAdd={handleImagesAdd}
                      onImageDelete={isFormEditable ? handleImageDelete : undefined} // 사전입찰 시작 후 삭제 불가
                      maxImages={3}
                      disabled={imageUploading || !isFormEditable} // 사전입찰 시작 후 비활성화
                    />
                  </ImageScrollContainer>
                </ImageFieldContainer>
                <ImageNotice>* 최소 1장, 최대 3장의 이미지를 첨부해야 합니다. (현재 {formData.thumbnailImages.length}장)</ImageNotice>
                <FileFormatNotice>WEBP, PNG, JPG, JPEG 파일만 가능</FileFormatNotice>
                {formErrors.thumbnailImages && <ErrorText>{formErrors.thumbnailImages}</ErrorText>}
              </ImageUploadField>
              
              <FormGroup>
                <AppTextField
                  value={formData.artworkName}
                  onChange={(e) => isFormEditable ? onInputChange('artworkName', e.target.value) : undefined}
                  label="*LOT명"
                  placeholder="LOT명을 입력하세요"
                  errorMessage={formErrors.artworkName}
                />
              </FormGroup>

              <FormGroup>
                <AppNumberTextField
                  label="*시작가 (KRW)"
                  value={formData.startPriceKRW || ''}
                  onChange={(e) => {
                    if (!isFormEditable) return;
                    const inputValue = e.target.value;
                    if (inputValue === '') {
                      onInputChange('startPriceKRW', null);
                    } else {
                      onInputChange('startPriceKRW', parseFloat(inputValue) || null);
                    }
                  }}
                  placeholder="1000"
                  errorMessage={formErrors.startPriceKRW}
                  allowDecimal={false}
                  unit="원"
                />
              </FormGroup>
            </Section>
          </LeftSection>

          <RightSection>
            <SectionTitle>LOT 설명</SectionTitle>
            <EditorContainer $isEditMode={isEditMode}>
              <CustomTiptapEditor
                key={`artwork-detail-${formData.auctioneerId}-${isOpen}`}
                content={formData.artworkDetail}
                onChange={handleEditorChange}
                placeholder="작품에 대한 상세 정보를 입력하세요..."
                height={isEditMode ? 1090 : 947} // 수정 모드일 때는 철회 섹션만큼 높이 감소
                readOnly={!isFormEditable} // 사전입찰 시작 후 읽기 전용
              />
            </EditorContainer>
          </RightSection>
        </Container>
      </Wrapper>
      
      {/* 삭제 확인 다이얼로그 */}
      {isDeleteModalOpen && (
        <DeleteModalOverlay onClick={handleDeleteCancel}>
          <DeleteModalContainer onClick={(e) => e.stopPropagation()}>
            <DeleteModalTitle>LOT 삭제</DeleteModalTitle>
            <DeleteModalMessage>
              LOT을 삭제하시겠습니까?{'\n'}
              삭제된 데이터는 복구할 수 없습니다.
            </DeleteModalMessage>
            <DeleteModalButtons>
              <DeleteModalButton onClick={handleDeleteCancel} variant="cancel">
                취소
              </DeleteModalButton>
              <DeleteModalButton onClick={handleDeleteConfirm} variant="delete">
                삭제
              </DeleteModalButton>
            </DeleteModalButtons>
          </DeleteModalContainer>
        </DeleteModalOverlay>
      )}

      {/* 스위치 변경 확인 모달 */}
      <Modal
        isOpen={isSwitchModalOpen}
        onClose={handleSwitchCancel}
        header={
          pendingSwitchValue 
            ? '해당 Lot의 경매를 철회하시겠습니까?' 
            : '해당 Lot의 경매를 재개하시겠습니까?'
        }
        body={
          pendingSwitchValue 
            ? '라이브 시작 시 "경매 철회 안내"\n푸시가 발송되며, 입찰이 중단됩니다.' 
            : '라이브 시작 시 "경매 재개 안내"\n푸시가 발송되며, 입찰이 다시 진행됩니다.'
        }
        bgColor="#ffffff"
        zIndex={10002}
        footer={
          <ModalButtonContainer>
            <CmsPopupBtn
              type={CmsPopupBtnType.SECONDARY}
              onClick={handleSwitchCancel}
            >
              취소
            </CmsPopupBtn>
            <CmsPopupBtn
              type={CmsPopupBtnType.PRIMARY}
              onClick={handleSwitchConfirm}
            >
              {pendingSwitchValue ? '철회' : '재개'}
            </CmsPopupBtn>
          </ModalButtonContainer>
        }
      />
      
      <AuctioneerSearchPopup
        isOpen={isAuctioneerPopupOpen}
        onClose={() => setIsAuctioneerPopupOpen(false)}
        onSelect={handleAuctioneerSelect}
      />
    </CmsPopup>
  );
};

export default LotFormPopup;

// Styled Components
const Wrapper = styled.div`
  padding-top: 20px;
  height: 100%;
`;

const Container = styled.div`
  display: flex;
  gap: 20px;
  height: 100%;
`;

const LeftSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RightSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const SectionTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e9ecef;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  text-align: left;
`;

const RequiredIndicator = styled.span`
  font-size: 14px;
  color: #dc3545;
  font-weight: 500;
`;

const Section = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 24px;
  background: #fafafa;
`;

const SubSectionTitle = styled.h4`
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 600;
  color: #495057;
  text-align: left;
`;

const WithdrawalHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  
  h4 {
    margin: 0;
  }
`;

const WithdrawalNotice = styled.div`
  color: #555555;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
`;

const ModalButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  
  & > * {
    flex: 1;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ErrorText = styled.span`
  color: #dc3545;
  font-size: 12px;
`;

const ImageUploadField = styled.div`
  position: relative;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 20px;
  background-color: #fafafa;
  margin-bottom: 20px;
`;

const ImageLabel = styled.label`
  position: absolute;
  top: -10px;
  left: 12px;
  background-color: white;
  padding: 0 8px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

const ImageFieldContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 20px 0;
  height: 200px;
  width: 100%;
  overflow: hidden;
`;

const ImageScrollContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  display: flex;
  align-items: center;
  white-space: nowrap;
  flex: 1;
  
  /* 내부 컨테이너가 늘어나지 않도록 제한 */
  & > * {
    flex-shrink: 0;
    max-width: none;
  }
  
  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const ImageNotice = styled.div`
  color: #dc3545;
  font-size: 12px;
  text-align: left;
  margin-top: 10px;
`;

const FileFormatNotice = styled.div`
  color: #6c757d;
  font-size: 12px;
  text-align: left;
  margin-top: 5px;
`;

const EditorContainer = styled.div<{ $isEditMode: boolean }>`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  
  .ProseMirror {
    min-height: ${props => props.$isEditMode ? '739px' : '843px'}; // 동적 높이 조정
    padding: 15px;
    border: 1px solid #ddd;
    border-radius: 4px;
  }
`;

// 삭제 확인 다이얼로그 스타일 - Modal 컴포넌트 스타일 참고
const DeleteModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 20000; /* CmsPopup보다 높은 z-index */
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: auto;
`;

const DeleteModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  
  background-color: #FFFFFF;
  padding: 24px 14px 12px 14px;
  border-radius: 14px;
  width: 300px;
  max-width: calc(100vw - 32px);
  min-width: 280px;
  z-index: 20001;
  
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const DeleteModalTitle = styled.div`
  text-align: center;
  font-size: 16px;
  font-weight: 760;
  color: #000000;
  margin-bottom: 16px;
  white-space: pre-line;
`;

const DeleteModalMessage = styled.div`
  font-size: 16px;
  line-height: 1.5;
  color: #000000;
  text-align: center;
  margin-bottom: 24px;
  flex-grow: 1;
  white-space: pre-line;
`;

const DeleteModalButtons = styled.div`
  display: flex;
  gap: 12px;
`;

interface DeleteModalButtonProps {
  variant: 'cancel' | 'delete';
}

const DeleteModalButton = styled.button<DeleteModalButtonProps>`
  border: none;
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
  border-radius: 8px;
  font-size: 1rem;
  padding: 0.75rem;
  flex: 1;
  
  ${props => props.variant === 'cancel' ? `
    background-color: transparent;
    border: 1px solid #6c757d;
    color: #6c757d;
    
    &:hover {
      background-color: #6c757d1A;
      color: #6c757d;
    }
  ` : `
    background-color: #dc3545;
    color: #FFFFFF;
    
    &:hover {
      background-color: #c82333;
    }
  `}
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    border-color: transparent;
  }
`;

// 제약사항 메시지 스타일
const RestrictionMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 20px;
  color: #856404;
  font-size: 14px;
  font-weight: 500;
`;

const RestrictionIcon = styled.span`
  font-size: 18px;
  line-height: 1;
`;

const RestrictionText = styled.span`
  flex: 1;
  line-height: 1.4;
`;
